import { ISelectionChangeArgs, ISelectItem } from "@components/inputs/select/Select.types";
import { IEntity } from "@odata/BindingContext";
import { IFieldInfo } from "@odata/FieldInfo.utils";
import React from "react";

import { BasicInputSizes, FieldType, LabelStatus, TextAlign } from "../../../enums";
import { TRecordAny } from "../../../global.types";
import TestIds from "../../../testIds";
import DateType from "../../../types/Date";
import memoizeOneWithParamsCheck from "../../../utils/memoizeOne";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ICheckboxChange } from "../../inputs/checkbox";
import Field from "../../inputs/field";
import { Select } from "../../inputs/select";
import { IFieldDef } from "../FieldInfo";
import SmartField, { ISmartFieldChange, ISmartFieldProps } from "../smartField/SmartField";
import SmartCollapseToggle from "../smartFormGroup/SmartCollapseToggle";
import { BulkChangeFieldWrapper, CheckboxStyled } from "./BulkChange.styles";
import {
    BulkChangeAction,
    BulkChangeActionPerFieldType,
    getBulkChangeActionTranslation,
    getDefaultBulkChangeAction,
    IBulkChangeDialogStorageCustomData
} from "./SmartBulkChange.utils";

export interface ISmartBulkChangeCheckboxChange {
    bindingContext: ISmartFieldProps["bindingContext"];
    isChecked: boolean;
}

interface IProps extends ISmartFieldProps {
    onCheckboxChange?: (args: ISmartBulkChangeCheckboxChange) => void;
    hasVisibleCollapsedFields: boolean;
}

type TBulkChangeFieldInfo = IFieldInfo & {
    bulkChangeAdditionalFields?: IFieldDef[];
};

export default class SmartBulkChangeField extends React.Component<IProps> {

    getInfo = memoizeOneWithParamsCheck(() => {
        return this.props.storage.getInfo(this.props.bindingContext);
    });

    getInitialCheckedValue = (): unknown => {
        const { bindingContext } = this.props;
        const storage = this.props.storage as FormStorage<IEntity, IBulkChangeDialogStorageCustomData>;
        const info = this.getInfo();
        const defaultValue = info.defaultValue !== undefined
            ? storage.getDefaultValue(bindingContext)
            : null;

        return defaultValue instanceof Date && !DateType.isValid(defaultValue)
            ? null
            : defaultValue;
    };

    handleCheckboxChange = (e: ICheckboxChange): void => {
        const { bindingContext } = this.props;
        const storage = this.props.storage as FormStorage<IEntity, IBulkChangeDialogStorageCustomData>;

        if (e.value) {
            storage.setValue(bindingContext, this.getInitialCheckedValue(), undefined, true);
        } else {
            // Clears completely value from storage with all additionalFields
            storage.clearValue(bindingContext, false, true);

            if (this.getInfo().defaultValue !== undefined) {
                storage.setValue(bindingContext, this.getInitialCheckedValue(), undefined, true);
            }
        }

        storage.setCustomData({
            isChecked: {
                ...storage.getCustomData().isChecked,
                [bindingContext.getNavigationPath()]: e.value
            }
        });
        this.props.onCheckboxChange?.({
            bindingContext,
            isChecked: e.value
        });
        storage.refresh(true);
    };

    handleFieldChange = (args: ISmartFieldChange): void => {
        this.props.storage.clearError(args.bindingContext);
        this.props.onChange(args);
        this.props.storage.refresh();
    };

    handleSelectChange = (args: ISelectionChangeArgs): void => {
        const { storage, bindingContext } = this.props;

        storage.clearError(bindingContext);
        storage.setAdditionalFieldData(bindingContext, "parsedValue", args.value);

        if (args.value === BulkChangeAction.RemoveAll || (args.value === BulkChangeAction.Remove && !bindingContext.isCollection() && this.getInfo().type !== FieldType.ComboBox)) {
            // cleanup all selected labels
            storage.setValue(bindingContext, null, undefined, true);
        }

        storage.refresh(true);
    };

    handleExpand = (): void => {
        this.props.storage.refresh();
    };

    get isChecked(): boolean {
        const { bindingContext } = this.props;
        const storage = this.props.storage as FormStorage<IEntity, IBulkChangeDialogStorageCustomData>;

        return !!storage.getCustomData()?.isChecked?.[bindingContext.getNavigationPath()];
    }

    isDisabled = (): boolean => {
        if (!this.isChecked) {
            return true;
        }

        const action = this.props.storage.getAdditionalFieldData(this.props.bindingContext, "parsedValue") as BulkChangeAction;

        if (action === BulkChangeAction.RemoveAll) {
            return true;
        }

        if (action === BulkChangeAction.Remove && !this.props.bindingContext.isCollection() && this.getInfo().type !== FieldType.ComboBox) {
            return true;
        }

        return false;
    };

    actionSelectItems = (): ISelectItem[] => {
        const info = this.getInfo();
        const actions = (info.allowedBulkChangeActions as BulkChangeAction[]) ?? BulkChangeActionPerFieldType[info.type];

        return actions.map((id) => ({
            id,
            label: getBulkChangeActionTranslation(id, this.props.storage.t)
        }));
    };

    renderExtraContent = (value: string): React.ReactElement => {
        const info = this.getInfo();
        const isDateField = info.type === FieldType.Date || this.props.bindingContext.getPath().startsWith("Date");

        return (
            <Field className={this.props.className}
                   style={this.props.style}
                   textAlign={TextAlign.Left}
                   type={FieldType.ComboBox}
                   isDisabled={!this.isChecked}
                   label={info.extraContentLabel ?? this.props.storage.t(`Common:BulkChange.${isDateField ? "DateAction" : "Action"}`)}
                   width={BasicInputSizes.L}>
                <Select value={value}
                        onChange={this.handleSelectChange}
                        items={this.actionSelectItems()}
                        isDisabled={!this.isChecked}
                        width={BasicInputSizes.L}
                />
            </Field>
        );
    };

    renderAdditionalFields = (fields: IFieldDef[], isDisabled: boolean): React.ReactElement[] => {
        return fields?.map(fieldRef => {
            const bc = this.props.storage.data.bindingContext.navigate(fieldRef.id);
            return (
                <SmartField
                    key={bc.toString()}
                    bindingContext={bc}
                    storage={this.props.storage}
                    onChange={this.handleFieldChange}
                    fieldProps={{
                        isDisabled
                    }}
                />
            );
        });
    };

    renderField = (field: React.ReactNode, info: IFieldInfo, data: TRecordAny): React.ReactNode => {
        const { storage, bindingContext } = this.props;
        if (data.props.isDisabled) {
            return null;
        }

        let extra = null;
        let extraValue = null;
        const isCollection = bindingContext.isCollection();
        // for EditableText, we change manually fieldDef the render function
        // => don't use the altered info passed from SmartField to get the original type
        const originalInfo = this.getInfo();

        if (BulkChangeActionPerFieldType[originalInfo.type]) {
            const parsedValue = storage.getAdditionalFieldData(bindingContext, "parsedValue") as BulkChangeAction;
            extraValue = parsedValue ?? getDefaultBulkChangeAction(isCollection);
            extra = this.renderExtraContent(extraValue);
        }
        const additionalFields = (originalInfo as TBulkChangeFieldInfo).bulkChangeAdditionalFields;

        let collapseToggle: React.ReactElement = null;
        if (originalInfo.collapsedRows?.length && originalInfo.noCollapsedExpansion && this.props.hasVisibleCollapsedFields) {
            collapseToggle = (
                <SmartCollapseToggle
                    storage={this.props.storage as FormStorage}
                    def={originalInfo}
                    isCollapsedGroupDisabled={!this.isChecked}
                    onExpand={this.handleExpand}
                />
            );
        }

        return (
            <BulkChangeFieldWrapper data-testid={TestIds.FormGroupLine}>
                <CheckboxStyled
                    _hasLabel={true}
                    checked={this.isChecked}
                    onChange={this.handleCheckboxChange}/>
                {extra}
                {field}
                {this.renderAdditionalFields(additionalFields, this.isDisabled())}
                {collapseToggle}
            </BulkChangeFieldWrapper>
        );
    };

    render() {
        // Local context fields are not intended to be used in bulk change (they are mostly dependent on custom form data)
        const { bindingContext } = this.props;
        if (bindingContext.isLocal()) {
            return null;
        }

        const { fieldProps, onChange, ...restProps } = this.props;

        let fieldDef: ISmartFieldProps["fieldDef"] = null;

        if (this.getInfo().type === FieldType.EditableText) {
            fieldDef = {
                labelStatus: LabelStatus.Visible,
                width: BasicInputSizes.XL
            };
        }

        return (
            <SmartField
                {...restProps}
                onChange={this.handleFieldChange}
                customRenderWrapper={this.renderField}
                fieldProps={{
                    ...fieldProps,
                    isDisabled: this.isDisabled()
                }}
                fieldDef={fieldDef}
            />
        );
    }
}
